import type { CSSProperties } from 'vue';
import type { CompiledCell, CompiledExtraRowCell, CompiledTableNode } from '@record-fill';
import type { TableProperties } from '../../model/form.types';
import { getResponsiveColumnTemplate } from '../../lib/table-column-utils';
import { TABLE_CONSTRAINTS } from '../../lib/table-constants';
import type { BorderSideName } from '../../lib/cell-border-geometry';

function tableProperties(node: CompiledTableNode): TableProperties {
  return node.element.properties as TableProperties;
}

// Mirrors TableGrid.vue: fr columns from the stored widths and a minmax row template, so
// the filled form measures exactly like the builder canvas.
export function fillGridStyles(node: CompiledTableNode): CSSProperties {
  const properties = tableProperties(node);
  const rows = Math.max(TABLE_CONSTRAINTS.MIN_ROWS, node.rows);
  const columns = Math.max(TABLE_CONSTRAINTS.MIN_COLUMNS, node.columns);
  const heights = node.rowHeights;
  const hasStoredHeights = !!heights && heights.length === rows;
  const gridTemplateRows = hasStoredHeights
    ? heights.map((height) => `minmax(${Math.max(height, TABLE_CONSTRAINTS.MIN_ROW_HEIGHT)}px, auto)`).join(' ')
    : `repeat(${rows}, minmax(${TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT}px, auto))`;

  return {
    display: 'grid',
    gridTemplateColumns: getResponsiveColumnTemplate(node.columnWidths, columns),
    gridTemplateRows,
    gap: '0',
    position: 'relative',
    width: `${properties.tableWidth ?? 100}%`,
  };
}

export interface ExtraGridCell {
  rowIndex: number;
  columnIndex: number;
  cell: CompiledCell;
}

export interface ExtraRowsGrid {
  table: CompiledTableNode;
  cells: ExtraGridCell[];
}

// Added rows live in the structure grid itself, as copies of the model-row cells moved below it,
// so padding, height and the collapsed borders come from the same resolver as the builder canvas.
// Drawing each added cell with its own full border doubled every shared edge.
export function extraRowsGrid(node: CompiledTableNode): ExtraRowsGrid {
  const modelRow = node.cells[node.cells.length - 1] ?? [];
  const modelHeight = node.rowHeights?.[node.rowHeights.length - 1];
  const rows = node.rows + node.extraRows.length;
  const rowHeights =
    node.rowHeights && modelHeight !== undefined
      ? [...node.rowHeights, ...node.extraRows.map(() => modelHeight)]
      : node.rowHeights;

  const cells: ExtraGridCell[] = [];
  node.extraRows.forEach((extraRow) => {
    const gridRow = node.rows + extraRow.rowIndex;
    extraRow.cells.forEach((extraCell: CompiledExtraRowCell) => {
      const model = modelRow.find((candidate) => candidate.colIndex === extraCell.columnIndex);
      if (!model) return;
      const id = `${node.id}-extra-${extraRow.rowIndex}-${extraCell.columnIndex}`;
      cells.push({
        rowIndex: extraRow.rowIndex,
        columnIndex: extraCell.columnIndex,
        cell: {
          ...model,
          id,
          rowIndex: gridRow,
          rowSpan: 1,
          node: extraCell.node,
          source: { ...model.source, id, rowIndex: gridRow, rowSpan: 1 },
        },
      });
    });
  });

  return { table: { ...node, rows, rowHeights }, cells };
}

export function multipleTableBarRowStyles(node: CompiledTableNode): CSSProperties {
  const properties = tableProperties(node);
  return {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '8px',
    width: `${properties.tableWidth ?? 100}%`,
  };
}

const ALIGNMENT_CLASS: Record<'left' | 'center' | 'right', string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

// The builder cell clips its content; the fill cell must not, or the active glow and the error
// outline of a control get cut at the 1px padding.
export function fillCellContentClass(cell: CompiledCell): string {
  return `flex h-full w-full min-w-0 max-w-full items-start box-border p-px ${ALIGNMENT_CLASS[cell.alignment ?? 'left']}`;
}

export type ChunkEdge = 'top' | 'bottom';

// The exported sheet cuts a table between rows; the cut edge gets the outer border, as the builder
// closes a page-break chunk. An overlay and not a real border, so no row grows after the sheet was
// measured, and the bottom one ends at the cut instead of bleeding into the next page.
export function chunkEdgeOverlayStyles(
  edge: ChunkEdge,
  outerBorder: string,
  widths: Record<BorderSideName, number>,
): CSSProperties {
  const span = { left: `-${widths.left}px`, right: `-${widths.right}px`, height: '0' };
  return edge === 'bottom'
    ? { ...span, bottom: `-${widths.bottom}px`, borderBottom: outerBorder }
    : { ...span, top: '0', borderTop: outerBorder };
}
